import { AdditiveBlending, Color, DoubleSide, ShaderMaterial } from 'three';
import { sharedUniforms } from '../core/FrameUniforms.js';
import { noiseGLSL } from '../shaders/lib/noise.glsl.js';

const VERTEX = /* glsl */ `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const FRAGMENT = /* glsl */ `
  uniform float uTime;
  uniform float uReveal;
  uniform float uFade;
  uniform float uRotation;
  uniform float uLayer;
  uniform float uIntensity;
  uniform float uGlobalGlow;
  uniform vec3 uEmber;
  uniform vec3 uOrange;
  uniform vec3 uGold;
  uniform vec3 uWhite;
  varying vec2 vUv;

  ${noiseGLSL}

  float band(float d, float w) { return exp(-pow(d / max(w, 0.001), 2.0)); }
  float polygon(vec2 p, float sides, float radius) {
    float a = atan(p.y, p.x) + 3.14159265;
    float sector = 6.2831853 / sides;
    return cos(floor(0.5 + a / sector) * sector - a) * length(p) - radius;
  }
  float segment(vec2 p, vec2 a, vec2 b) {
    vec2 pa = p - a;
    vec2 ba = b - a;
    float h = clamp(dot(pa, ba) / dot(ba, ba), 0.0, 1.0);
    return length(pa - ba * h);
  }

  void main() {
    vec2 p = (vUv - 0.5) * 2.0;
    float r = length(p);
    if (r > 1.03) discard;
    float cs = cos(uRotation);
    float sn = sin(uRotation);
    vec2 q = mat2(cs, -sn, sn, cs) * p;
    float a = atan(q.y, q.x);
    float ring = 0.0;
    float hot = 0.0;

    if (uLayer < 0.5) {
      // Dense engraved circumference: three rails surrounding thirty-six
      // individually varied pseudo-runes, like an inscription rather than ticks.
      ring += band(abs(r - 0.975), 0.009) * 1.45;
      ring += band(abs(r - 0.925), 0.008) * 0.72;
      ring += band(abs(r - 0.825), 0.012) * 1.15;
      float cellPosition = (a + 3.14159265) / 6.2831853 * 36.0;
      float cell = floor(cellPosition);
      vec2 runeP = vec2((fract(cellPosition) - 0.5) * 2.0, (r - 0.875) / 0.043);
      float runeHash = fract(sin((cell + 4.0) * 91.713) * 43758.5453);
      float rune = band(segment(runeP, vec2(-0.42, -0.52), vec2(0.36, 0.48)), 0.09);
      rune += band(segment(runeP, vec2(-0.38, 0.46), vec2(0.28 - runeHash * 0.3, -0.48)), 0.085);
      rune += band(segment(runeP, vec2(-0.22, 0.02), vec2(0.42, 0.02 + runeHash * 0.28)), 0.08);
      rune *= 1.0 - smoothstep(0.82, 1.02, abs(runeP.y));
      float dividers = pow(max(0.0, cos(a * 36.0)), 52.0) * band(abs(r - 0.875), 0.062);
      ring += rune * 0.78 + dividers * 0.85;
      hot = rune * 0.45 + dividers;
    } else if (uLayer < 1.5) {
      // Five-node celestial lattice. Crossing every second vertex creates the
      // large woven star while the medallions interrupt it cleanly.
      float star = 0.0;
      float nodes = 0.0;
      float nodeMarks = 0.0;
      for (int i = 0; i < 5; i++) {
        float fi = float(i);
        float a0 = fi * 1.25663706 - 1.5707963;
        float a1 = (fi + 2.0) * 1.25663706 - 1.5707963;
        vec2 v0 = vec2(cos(a0), sin(a0)) * 0.71;
        vec2 v1 = vec2(cos(a1), sin(a1)) * 0.71;
        star += band(segment(q, v0, v1), 0.012);

        vec2 nodeP = (q - v0) / 0.125;
        nodes += band(abs(length(nodeP) - 1.0), 0.075);
        float variant = fract(sin((fi + 2.0) * 37.71) * 418.31);
        nodeMarks += band(segment(nodeP, vec2(-0.38, -0.42), vec2(0.3, 0.38)), 0.075);
        nodeMarks += band(segment(nodeP, vec2(-0.32, 0.28), vec2(0.4, -0.18 + variant * 0.25)), 0.07);
        nodeMarks += band(abs(length(nodeP - vec2(0.02, -0.04)) - (0.28 + variant * 0.08)), 0.055) * 0.6;
      }
      float webRing = band(abs(r - 0.73), 0.011) + band(abs(r - 0.49), 0.009) * 0.55;
      float spokes = pow(max(0.0, cos(a * 10.0)), 58.0) * smoothstep(0.28, 0.75, r);
      ring = star * 0.82 + nodes * 1.45 + nodeMarks * 0.72 + webRing + spokes * 0.52;
      hot = nodes + nodeMarks * 0.6;
    } else {
      // Concentric focus seal with a hand-drawn angular monogram at its heart.
      ring += band(abs(r - 0.405), 0.012) * 1.15;
      ring += band(abs(r - 0.315), 0.011) * 0.72;
      ring += band(abs(r - 0.205), 0.014) * 1.3;
      float petals = pow(abs(sin(a * 10.0)), 28.0) * band(abs(r - 0.355), 0.075);
      float spokes = pow(max(0.0, cos(a * 8.0)), 50.0) * smoothstep(0.19, 0.4, r);
      vec2 m = q / 0.2;
      float monogram = band(segment(m, vec2(-0.58, -0.55), vec2(-0.06, 0.58)), 0.075);
      monogram += band(segment(m, vec2(-0.4, 0.05), vec2(0.48, 0.43)), 0.07);
      monogram += band(segment(m, vec2(-0.35, -0.12), vec2(0.52, -0.46)), 0.07);
      monogram += band(segment(m, vec2(0.34, -0.58), vec2(0.02, 0.52)), 0.065);
      float orbit = band(abs(length(m - vec2(0.02, 0.0)) - 0.64), 0.045) * 0.55;
      ring += petals * 0.62 + spokes * 0.45 + monogram * 1.5 + orbit;
      hot = monogram + petals * 0.35;
    }

    float sweep = smoothstep(uReveal - 0.16, uReveal, 1.0 - abs(a) / 3.14159265);
    float radialReveal = 1.0 - smoothstep(uReveal - 0.08, uReveal + 0.08, r);
    float reveal = max(sweep, radialReveal) * smoothstep(0.0, 0.09, uReveal);
    float shimmer = 0.82 + 0.18 * snoise(vec3(p * 8.0, uTime * 2.4 + uLayer * 7.0));
    float pulse = 0.88 + 0.12 * sin(uTime * (5.0 + uLayer * 1.7) + r * 21.0);
    float alpha = ring * reveal * uFade * shimmer;
    if (alpha < 0.008) discard;

    vec3 color = mix(uEmber, uOrange, smoothstep(0.05, 0.55, ring));
    color = mix(color, uGold, clamp(hot, 0.0, 1.0));
    color = mix(color, uWhite, pow(clamp(ring, 0.0, 1.0), 5.0) * 0.38);
    color *= uIntensity * uGlobalGlow * pulse;
    gl_FragColor = vec4(color, min(1.0, alpha));
  }
`;

export function createRunicGlyphMaterial(layer) {
  return new ShaderMaterial({
    transparent: true,
    depthWrite: false,
    depthTest: true,
    blending: AdditiveBlending,
    side: DoubleSide,
    toneMapped: false,
    uniforms: sharedUniforms({
      uReveal: { value: 0 },
      uFade: { value: 1 },
      uRotation: { value: 0 },
      uLayer: { value: layer },
      uIntensity: { value: 1 },
      uEmber: { value: new Color('#8f2100') },
      uOrange: { value: new Color('#ff6a16') },
      uGold: { value: new Color('#ffb52e') },
      uWhite: { value: new Color('#fff3c4') }
    }),
    vertexShader: VERTEX,
    fragmentShader: FRAGMENT
  });
}

export function syncRunicGlyphMaterial(material, config, state) {
  const u = material.uniforms;
  u.uReveal.value = state.reveal;
  u.uFade.value = state.fade;
  u.uRotation.value = state.rotation;
  u.uIntensity.value = config.shaderIntensity;
  u.uEmber.value.set(config.colorEmber);
  u.uOrange.value.set(config.colorOrange);
  u.uGold.value.set(config.colorGold);
  u.uWhite.value.set(config.colorWhite);
}
